import React, { useEffect, useState, useCallback, useMemo } from "react";
import { ArrowLeft, BookOpen, Search, ChevronDown, Star } from "lucide-react";
import PageHero, { EYEBROW } from "@/components/PageHero";
import SEO from "@/components/SEO";
import Reveal from "@/components/motion/Reveal";
import Section from "@/components/ui/Section";
import Card from "@/components/ui/Card";
import CTAButton, { ArrowLink } from "@/components/ui/CTAButton";
import SmartImage from "@/components/ui/SmartImage";
import EmptyState from "@/components/ui/EmptyState";
import { Skeleton } from "@/components/ui/Skeleton";
import { normalizeImgUrl } from "@/lib/utils-img";
import { apiGet } from "@/lib/api";

const PAGE_SIZE = 30;

const fmtDate = d =>
  d
    ? new Date(`${d}T00:00:00`).toLocaleDateString("es-ES", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "";

/** Miniatura que se esconde sola si el enlace está roto, igual que en la app. */
function GospelThumb({ imageUrl, alt }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [imageUrl]);

  if (!imageUrl || failed) {
    return (
      <div className="w-20 h-20 rounded-xl flex-shrink-0 bg-primary/10 flex items-center justify-center">
        <BookOpen className="w-6 h-6 text-primary" aria-hidden="true" />
      </div>
    );
  }
  return (
    <img
      src={normalizeImgUrl(imageUrl)}
      alt={alt}
      width={80}
      height={80}
      className="w-20 h-20 rounded-xl object-cover flex-shrink-0"
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
    />
  );
}

function GospelCard({ gospel, expanded, onToggle }) {
  const fecha = fmtDate(gospel.date);
  const panelId = `evangelio-${gospel._id || gospel.date}`;

  return (
    <Card as="article" data-testid="gospel-archive-item" className="rounded-2xl">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={panelId}
        className="w-full text-left p-5 flex items-start gap-4 focus-ring"
      >
        <GospelThumb imageUrl={gospel.image_url || gospel.image} alt="" />
        <div className="flex-1 min-w-0">
          {fecha && (
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-1.5 first-letter:uppercase">
              {fecha}
            </p>
          )}
          <p className="text-sm font-semibold text-primary mb-1.5">{gospel.reference}</p>
          <p className={`font-spiritual italic text-base leading-relaxed ${expanded ? "" : "line-clamp-2"}`}>
            {gospel.text}
          </p>
        </div>
        <span className="flex-shrink-0 text-muted-foreground mt-1" aria-hidden="true">
          <ChevronDown
            className={`w-5 h-5 transition-transform duration-base ease-out-apple ${expanded ? "rotate-180" : ""}`}
          />
        </span>
      </button>

      {expanded && (
        <div id={panelId} className="px-5 pb-5 space-y-5">
          {(gospel.image_url || gospel.image) && (
            // El contenedor solo tenía `max-height`, sin altura reservada, así
            // que al desplegar la tarjeta el contenido de abajo saltaba cuando
            // la imagen terminaba de decodificar.
            <SmartImage
              src={gospel.image_url || gospel.image}
              alt={`Ilustración del Evangelio del ${fecha}`}
              ratio="16 / 9"
              rounded="rounded-xl"
            />
          )}

          {gospel.reflection && (
            <div className="bg-muted rounded-xl p-6 border border-border" data-testid="gospel-archive-reflection">
              <div className="flex items-center gap-2 mb-4">
                <BookOpen className="w-4 h-4 text-primary" aria-hidden="true" />
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-primary">Reflexión</p>
              </div>
              <p className="text-base leading-relaxed whitespace-pre-line">{gospel.reflection}</p>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

export default function EvangeliosAnteriores() {
  const [gospels, setGospels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [reachedEnd, setReachedEnd] = useState(false);
  const [error, setError] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [query, setQuery] = useState("");

  const fetchPage = useCallback(async skip => {
    const data = await apiGet(`/word-of-day/history?skip=${skip}&limit=${PAGE_SIZE}`);
    return Array.isArray(data) ? data : [];
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchPage(0)
      .then(page => {
        if (cancelled) return;
        setGospels(page);
        setReachedEnd(page.length < PAGE_SIZE);
      })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [fetchPage]);

  const loadMore = async () => {
    if (loadingMore || reachedEnd) return;
    setLoadingMore(true);
    try {
      const page = await fetchPage(gospels.length);
      setGospels(prev => [...prev, ...page]);
      if (page.length < PAGE_SIZE) setReachedEnd(true);
    } catch {
      // Se reintenta con el botón; un fallo de red no marca el final del archivo.
    } finally {
      setLoadingMore(false);
    }
  };

  // El buscador filtra lo ya descargado, igual que en la app: la paginación
  // trae el archivo por bloques y la búsqueda afina sobre lo que hay en pantalla.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return gospels;
    return gospels.filter(g =>
      [g.date, fmtDate(g.date), g.reference, g.text, g.reflection]
        .filter(Boolean)
        .some(field => String(field).toLowerCase().includes(q))
    );
  }, [gospels, query]);

  return (
    <div data-testid="evangelios-anteriores-page">
      <SEO
        title="Evangelios anteriores"
        description="Archivo de los evangelios y reflexiones publicados por la Delegación de Evangelización Digital de la Diócesis de Huelva."
      />

      {/* Otro de los cuatro héroes que se dibujaban a mano; ahora reutiliza
          PageHero con contenido propio. */}
      <PageHero
        content={{
          subtitle: EYEBROW,
          title: "Evangelios",
          title_italic: "anteriores",
        }}
      />

      <Section tone="light" width="narrow">
        <ArrowLink
          to="/palabra-del-dia"
          data-testid="back-to-palabra-link"
          className="text-muted-foreground hover:text-primary mb-8"
        >
          <ArrowLeft className="w-4 h-4" aria-hidden="true" /> Volver a la Palabra del Día
        </ArrowLink>

        {/* Buscador */}
        <div className="relative mb-8">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" aria-hidden="true" />
          <label htmlFor="buscar-evangelios" className="sr-only">
            Buscar en el archivo de evangelios
          </label>
          <input
            id="buscar-evangelios"
            type="search"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Buscar por fecha, cita o texto…"
            data-testid="gospel-archive-search"
            className="w-full pl-11 pr-4 py-3 rounded-full border border-border bg-card text-sm focus-ring"
          />
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4].map(i => (
              <Skeleton key={i} className="h-32 rounded-2xl" />
            ))}
          </div>
        ) : error ? (
          <EmptyState
            icon={Star}
            title="No se ha podido cargar el archivo"
            description="Inténtalo de nuevo en unos minutos."
          />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={Star}
            title={query ? "Ningún evangelio coincide con la búsqueda" : "Todavía no hay evangelios anteriores"}
            description={query ? "Prueba con otra fecha, cita o palabra." : "En cuanto se publiquen aparecerán aquí."}
          />
        ) : (
          <>
            <div className="space-y-4">
              {filtered.map((g, i) => (
                <Reveal key={g._id || g.date} delay={Math.min(i, 6) * 50} distance={20}>
                  <GospelCard
                    gospel={g}
                    expanded={expandedId === (g._id || g.date)}
                    onToggle={() =>
                      setExpandedId(prev => (prev === (g._id || g.date) ? null : g._id || g.date))
                    }
                  />
                </Reveal>
              ))}
            </div>

            {!reachedEnd && (
              <div className="flex justify-center mt-10">
                <CTAButton
                  onClick={loadMore}
                  disabled={loadingMore}
                  data-testid="gospel-archive-load-more"
                  className="font-semibold"
                >
                  {loadingMore ? "Cargando…" : "Ver más evangelios"}
                </CTAButton>
              </div>
            )}
          </>
        )}
      </Section>
    </div>
  );
}
